import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { useState, type ReactNode } from 'react';

interface ConfirmDialogProps {
  title: string;
  /**
   * What the action does, in the words an administrator needs in order to decide. Not a warning
   * and not a repeat of the title: the consequence, and who feels it.
   */
  children: ReactNode;
  confirmLabel: string;
  confirmColor?: 'error' | 'warning';
  onClose: () => void;
  onConfirm: () => Promise<unknown> | unknown;
}

/**
 * The one confirmation dialog, for the panel actions somebody else feels immediately — a seat
 * taken off a running device, a licence locked, a file removed.
 *
 * Ported from SuPAR Insight's `DeleteConfirmationDialog`, with its string table dropped: this
 * panel has no i18n, and a shared dialog with fixed "Are you sure?" copy would be worse than no
 * dialog at all. A confirmation that says nothing new trains the administrator to click through
 * it, so every call site writes its own consequence and the component only carries the shape.
 *
 * **Cancel keeps the focus.** MUI focuses the first button, which is Cancel, and that is
 * deliberate: the dialog exists to interrupt a reflex, and a confirm button under Enter is not an
 * interruption. Insight autofocuses its delete button; this is the one place it is not copied.
 *
 * It is rendered only while something is pending, so the copy can name that item without
 * defending against it being undefined half the time.
 */
const ConfirmDialog = ({
  title, children, confirmLabel, confirmColor = 'error', onClose, onConfirm
}: ConfirmDialogProps) => {
  const [isWorking, setWorking] = useState(false);

  const confirm = async () => {
    setWorking(true);

    try {
      // What went wrong already reaches the administrator through the endpoint's snackbar; all
      // this has to do is not close as though it had worked.
      await onConfirm();
    } catch (error) {
      console.error('confirm.error:', error);
      setWorking(false);

      return;
    }

    setWorking(false);
    onClose();
  };

  return (
    <Dialog
      open
      onClose={isWorking ? undefined : onClose}
      fullWidth
      maxWidth='xs'
      aria-labelledby='confirm-dialog-title'
      aria-describedby='confirm-dialog-description'
    >
      <DialogTitle id='confirm-dialog-title'>{title}</DialogTitle>

      <DialogContent>
        <DialogContentText id='confirm-dialog-description' component='div'>
          {children}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isWorking}>Cancel</Button>
        <Button onClick={confirm} color={confirmColor} variant='contained' disabled={isWorking}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
